/**
 * Number of day columns a VP period carries, counted inclusively.
 *
 * VP's semi-monthly second-half period runs 16 days in every 31-day month, so
 * the count comes from the period's own bounds. Returns 0 when either bound is
 * missing, which is how the period reads before the store has loaded.
 */
export function periodDayCount(periodStart: string, periodEnd: string): number {
  const start = parseIsoDate(periodStart);
  const end = parseIsoDate(periodEnd);
  if (start === null || end === null) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}

/**
 * A date as MM/DD, e.g. "09/22". Fixed format: there is no locale setting.
 * Returns the input unchanged when it is not a YYYY-MM-DD date.
 */
export function formatMonthDay(iso: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[1]}/${m[2]}` : iso;
}

/** A date range as "MM/DD – MM/DD", or "MM/DD" when it is a single day. */
export function formatDateRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return formatMonthDay(startIso);
  return `${formatMonthDay(startIso)} – ${formatMonthDay(endIso)}`;
}

const DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Short label for a period-relative day, e.g. "Sat 16" — the shape the grid's
 * column header carries, so a day named elsewhere points at a column the user
 * can see. Empty when the period start is unreadable.
 */
export function periodDayLabel(periodStart: string, dayIndex: number): string {
  const start = parseIsoDate(periodStart);
  if (start === null) return '';
  const d = new Date(start + dayIndex * 86_400_000);
  return `${DOWS[d.getUTCDay()]} ${d.getUTCDate()}`;
}

/**
 * Where a source period's day lands in the target period, matched by weekday
 * occurrence — the nth Monday of one period is the nth Monday of the other.
 *
 * Periods here are semi-monthly and unequal in length, so a day-index map drops
 * a Monday stand-up onto a Saturday. Returns null where the target period has
 * no such occurrence (a shorter period runs out of Mondays before the source
 * does), and null when either period start is unreadable.
 */
export function weekdayAlignedDayIndex(
  sourcePeriodStart: string,
  sourceDayIndex: number,
  targetPeriodStart: string,
  targetDayCount: number,
): number | null {
  const source = parseIsoDate(sourcePeriodStart);
  const target = parseIsoDate(targetPeriodStart);
  if (source === null || target === null) return null;

  const dow = new Date(source + sourceDayIndex * 86_400_000).getUTCDay();
  // Weekdays repeat every seven days, so the day's position in the run of its
  // own weekday is its index over seven.
  const occurrence = Math.floor(sourceDayIndex / 7);
  const first = (dow - new Date(target).getUTCDay() + 7) % 7;
  const dayIndex = first + occurrence * 7;
  return dayIndex < targetDayCount ? dayIndex : null;
}

function parseIsoDate(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
